import type { CircuitIR, DrcFinding } from "@models/circuit-ir";
/**
 * AskAgentToFixButton — closes the DRC value loop.
 *
 * Per IR plan §6.7, every Finding carries a `suggested_fix: list[CircuitOp]`
 * the agent can replay to resolve the issue. This button turns that into
 * a one-click affordance: click → composer prefilled with a structured
 * "Apply this fix:" prompt scoped to the finding → user hits Enter →
 * agent runs `mutate_circuit` with the suggested ops.
 *
 * Today the suggested-fix typed-ops aren't yet exposed in the API, so
 * we send the agent a humanized prompt that references the finding's
 * code, message, and the resolved refdes / net names. When the typed
 * ops surface, we'll attach them inline so the agent gets a deterministic
 * recipe instead of having to re-derive the fix.
 *
 * Visual treatment: the button is a small primary-tinted pill with the
 * AutoFixHigh sparkle icon — leans into the "magic wand" affordance so
 * it reads as an *agentic* action, distinct from the Confirm/Reject
 * buttons elsewhere in the panel.
 */
import AutoFixIcon from "@mui/icons-material/AutoFixHighRounded";
import { Tooltip } from "@mui/material";
import { Button } from "@mui/material";
import { useChatStore } from "@stores/chatStore";
import { useDraftStore } from "@stores/draftStore";

interface AskAgentToFixButtonProps {
  finding: DrcFinding;
  /** IR snapshot the finding was raised against — drives refdes / net
   *  resolution so the agent prompt references real names instead of
   *  bare UUIDs. */
  ir?: CircuitIR | null;
  /** When provided, scopes the agent's prompt to this snapshot id so
   *  the mutate_circuit call lands on the right working buffer. */
  snapshotId?: string | null;
  /** Closes a parent drawer / panel after the prompt fires (so the
   *  user's eye lands back on the chat input). Defaults to no-op. */
  onAfterSend?: () => void;
}

function buildPrompt(
  finding: DrcFinding,
  ir: CircuitIR | null | undefined,
  snapshotId: string | null | undefined,
): string {
  const componentLabels = ir
    ? finding.component_ids
        .map((id) => ir.components.find((c) => c.id === id)?.refdes)
        .filter((r): r is string => !!r)
    : [];
  const netLabels = ir
    ? finding.net_ids
        .map((id) => ir.nets.find((n) => n.id === id)?.name)
        .filter((n): n is string => !!n)
    : [];

  // Deliberately structured prompt — the agent has been trained to
  // recognise the "Apply this fix" idiom and route through
  // mutate_circuit + validate without a long preamble.
  const lines: string[] = [
    `Apply a fix for DRC finding \`${finding.code}\` (${finding.severity}):`,
    "",
    `> ${finding.message}`,
  ];
  if (componentLabels.length > 0) {
    lines.push("", `Component(s): ${componentLabels.join(", ")}`);
  }
  if (netLabels.length > 0) {
    lines.push(`Net(s): ${netLabels.join(", ")}`);
  }
  if (snapshotId) {
    lines.push("", `Operate on snapshot \`${snapshotId}\`.`);
  }
  lines.push(
    "",
    "Use mutate_circuit to apply the smallest set of typed ops that resolves the finding, then call validate to confirm.",
  );
  return lines.join("\n");
}

export function AskAgentToFixButton({
  finding,
  ir,
  snapshotId,
  onAfterSend,
}: AskAgentToFixButtonProps) {
  const prefillComposer = useChatStore((s) => s.prefillComposer);
  // Optional: when the panel is open we collapse it so the user's
  // eye lands on the chat composer they just got prefilled.
  const collapsePanel = useDraftStore((s) => s.collapsePanel);

  function handleClick() {
    prefillComposer(buildPrompt(finding, ir, snapshotId));
    collapsePanel();
    onAfterSend?.();
  }

  return (
    <Tooltip title="Send this to the agent — it will mutate the snapshot and re-validate.">
      <Button
        size="small"
        variant="outlined"
        startIcon={<AutoFixIcon fontSize="small" />}
        onClick={handleClick}
        sx={(t) => ({
          borderColor: t.palette.accent.main,
          color: t.palette.accent.main,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.01em",
          px: 1,
          py: 0.25,
          minHeight: 24,
          // Phosphor halo on hover — single brand-defining flourish so
          // the user reads this as the "magic wand" affordance.
          transition: `border-color ${t.motion.fast}, background-color ${t.motion.fast}, box-shadow ${t.motion.fast}`,
          "&:hover": {
            borderColor: t.palette.accent.main,
            backgroundColor: t.palette.accent.softer,
            boxShadow: t.palette.accent.glow,
          },
        })}
      >
        Ask agent to fix
      </Button>
    </Tooltip>
  );
}
